/**
 * Confirm — replaces window.confirm(). `const confirm = useConfirm()` then
 * `if (!(await confirm({ title }))) return;`. Mount <ConfirmProvider> once
 * near the app root.
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';

interface ConfirmOpts {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}
type ConfirmFn = (opts: ConfirmOpts) => Promise<boolean>;

const ConfirmCtx = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal open={!!opts} onClose={() => close(false)} title={opts?.title}>
        {opts?.message && <p className="text-sm text-gray-600 mb-4">{opts.message}</p>}
        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={() => close(false)}>
            {opts?.cancelText ?? 'ยกเลิก'}
          </button>
          <button
            className={`flex-1 ${opts?.danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => close(true)}
          >
            {opts?.confirmText ?? 'ยืนยัน'}
          </button>
        </div>
      </Modal>
    </ConfirmCtx.Provider>
  );
}
